import { Action, Reducer } from 'redux';
import { AppThunkAction } from '..';
import {
  getDateWithoutTimezone,
  getRequestOptions,
  postRequestOptions,
} from '../../utils/utils';
import { FetchError } from '../../Interfaces/FetchError';
import {
  ReportGridPageQueryResult,
  ReportQueryResult,
} from '../../Interfaces/Reports/ReportQueryResult';
import { ReportCategory } from '../../Interfaces/Reports/ReportCategory';
import {
  ReportQueryGridPageByNode,
  ReportQueryGridPageByNodePayload,
  ReportQueryInfoByNode,
  ReportQueryInfoByNodePayload,
} from '../../Interfaces/Reports/ReportQueryInfoByNode';
import {
  ReportQueryGridPageByStore,
  ReportQueryGridPageByStorePayload,
  ReportQueryInfoByStore,
  ReportQueryInfoByStorePayload,
} from '../../Interfaces/Reports/ReportQueryInfoByStore';
import { ReportNodeInfo } from '../../Interfaces/Reports/ReportNodeInfo';
import {
  ReportExportRequestBody,
  ReportExportRequestBodyPayload,
  ReportExportSource,
} from '../../Interfaces/Reports/ReportExportRequestBody';
import { ReportExportResult } from '../../Interfaces/Reports/ReportExportResult';
import {
  ReportExportStatus,
  ReportExportStatusList,
} from '../../Interfaces/Reports/ReportExportStatus';
import { ReportDownloadURLRequestBody } from '../../Interfaces/Reports/ReportDownloadURLRequestBody';
import { ReportDownloadURLResponseResult } from '../../Interfaces/Reports/ReportDownloadURLResponseResult';
import { ReportExportListRequest } from '../../Interfaces/Reports/ReportExportListRequest';

export enum ReportsActionTypes {
  RESET_STATE = 'RESET_STATE',
  UNAUTHORIZED_REQUEST = 'UNAUTHORIZED_REQUEST',
  RECEIVED_HTTP_RESPONSE_ERROR_CODE = 'RECEIVED_HTTP_RESPONSE_ERROR_CODE',
  RESET_HTTP_RESPONSE_FETCH_ERROR = 'RESET_HTTP_RESPONSE_FETCH_ERROR',
  RECEIVED_DIVISIONS = 'RECEIVED_DIVISIONS',
  RECEIVED_STORE_NUMBERS = 'RECEIVED_STORE_NUMBERS',
  RECEIVED_LANE_NUMBERS = 'RECEIVED_LANE_NUMBERS',
  REQUEST_DIVISIONS = 'REQUEST_DIVISIONS',
  REQUEST_STORE_NUMBERS = 'REQUEST_STORE_NUMBERS',
  REQUEST_LANE_NUMBERS = 'REQUEST_LANE_NUMBERS',
  RECEIVED_REPORT_CATEGORIES = 'RECEIVED_REPORT_CATEGORIES',
  REQUEST_REPORT_DATA = 'REQUEST_REPORT_DATA',
  RECEIVED_REPORT_DATA = 'RECEIVED_REPORT_DATA',
  REQUEST_REPORT_DATA_PAGE = 'REQUEST_REPORT_DATA_PAGE',
  RECEIVED_REPORT_DATA_PAGE = 'RECEIVED_REPORT_DATA_PAGE',
  SET_PANEL_BAR_VISIBLE = 'SET_PANEL_BAR_VISIBLE',
  REQUEST_REPORT_EXPORT_DATA = 'REQUEST_REPORT_EXPORT_DATA',
  RECEIVED_REPORT_EXPORT_DATA_RESULT = 'RECEIVED_REPORT_EXPORT_DATA_RESULT',
  REQUEST_REPORT_EXPORT_STATUS_LIST = 'REQUEST_REPORT_EXPORT_STATUS_LIST',
  RECEIVED_REPORT_EXPORT_STATUS_LIST = 'RECEIVED_REPORT_EXPORT_STATUS_LIST',
  REQUEST_REPORT_DOWNLOAD_URL = 'REQUEST_REPORT_DOWNLOAD_URL',
  RECEIVED_REPORT_DOWNLOAD_URL = 'RECEIVED_REPORT_DOWNLOAD_URL',
  RESET_REPORT_DOWNLOAD_URL = 'RESET_REPORT_DOWNLOAD_URL',
}

export interface ReportsState {
  unauthorizedRequest: boolean;
  fetchError: FetchError;
  isFetchingReportData: boolean;
  divisions: string[];
  stores: string[];
  reportNodeInfo: ReportNodeInfo[];
  categories: ReportCategory[];
  reportData: ReportQueryResult;
  panelBarVisible: boolean;
  isFetchingDivisions: boolean;
  isFetchingStores: boolean;
  isFetchingReportNodeInfo: boolean;
  isRequestingReportExportData: boolean;
  reportExportResult: ReportExportResult;
  isExportResultReceived: boolean;
  isFetchingReportExportStatusList: boolean;
  reportExportStatusList: ReportExportStatus[];
  isFetchingReportDownloadURL: boolean;
  reportDownloadURL: ReportDownloadURLResponseResult;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
interface ResetStateAction {
  type: ReportsActionTypes.RESET_STATE;
}

interface UnauthorizedRequestAction {
  type: ReportsActionTypes.UNAUTHORIZED_REQUEST;
}

interface ResetHttpResponseError {
  type: ReportsActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR;
}

interface ReceivedErrorCode {
  type: ReportsActionTypes.RECEIVED_HTTP_RESPONSE_ERROR_CODE;
  statusCode: number;
}

interface ReceivedDivisionsAction {
  type: ReportsActionTypes.RECEIVED_DIVISIONS;
  divisions: string[];
}

interface ReceivedStoresAction {
  type: ReportsActionTypes.RECEIVED_STORE_NUMBERS;
  stores: string[];
}

interface ReceivedLaneNumbersAction {
  type: ReportsActionTypes.RECEIVED_LANE_NUMBERS;
  reportNodeInfo: ReportNodeInfo[];
}

interface ReceivedCategoriesAction {
  type: ReportsActionTypes.RECEIVED_REPORT_CATEGORIES;
  categories: ReportCategory[];
}

interface RequestReportDataAction {
  type: ReportsActionTypes.REQUEST_REPORT_DATA;
}

interface ReceivedReportDataAction {
  type: ReportsActionTypes.RECEIVED_REPORT_DATA;
  queryResult: ReportQueryResult;
}

interface RequestReportDataPageAction {
  type: ReportsActionTypes.REQUEST_REPORT_DATA_PAGE;
}

interface ReceivedReportDataPageAction {
  type: ReportsActionTypes.RECEIVED_REPORT_DATA_PAGE;
  queryResult: ReportGridPageQueryResult;
}

interface SetPanelBarVisible {
  type: ReportsActionTypes.SET_PANEL_BAR_VISIBLE;
  showPanelBar: boolean;
}

interface RequestDivisionsAction {
  type: ReportsActionTypes.REQUEST_DIVISIONS;
  refreshing: boolean;
}

interface RequestStoresAction {
  type: ReportsActionTypes.REQUEST_STORE_NUMBERS;
  refreshing: boolean;
}

interface RequestLanesAction {
  type: ReportsActionTypes.REQUEST_LANE_NUMBERS;
  refreshing: boolean;
}

interface RequestReportExportDataAction {
  type: ReportsActionTypes.REQUEST_REPORT_EXPORT_DATA;
  isRequestingReportExportData: boolean;
}

interface ReceivedReportExportDataAction {
  type: ReportsActionTypes.RECEIVED_REPORT_EXPORT_DATA_RESULT;
  reportExportResult: ReportExportResult;
}

interface RequestReportExportStatusListAction {
  type: ReportsActionTypes.REQUEST_REPORT_EXPORT_STATUS_LIST;
  isRequesting: boolean;
}

interface ReceivedReportExportStatusListAction {
  type: ReportsActionTypes.RECEIVED_REPORT_EXPORT_STATUS_LIST;
  reportExportStatusList: ReportExportStatusList;
}

interface RequestReportDownloadURL {
  type: ReportsActionTypes.REQUEST_REPORT_DOWNLOAD_URL;
  isRequesting: boolean;
}

interface ReceivedReportDownloadURL {
  type: ReportsActionTypes.RECEIVED_REPORT_DOWNLOAD_URL;
  responseResult: ReportDownloadURLResponseResult;
}

interface ResetReportDownloadURL {
  type: ReportsActionTypes.RESET_REPORT_DOWNLOAD_URL;
}

type KnownAction =
  | ResetStateAction
  | UnauthorizedRequestAction
  | ResetHttpResponseError
  | ReceivedErrorCode
  | ReceivedDivisionsAction
  | ReceivedStoresAction
  | ReceivedLaneNumbersAction
  | ReceivedCategoriesAction
  | RequestReportDataAction
  | ReceivedReportDataAction
  | RequestReportDataPageAction
  | ReceivedReportDataPageAction
  | SetPanelBarVisible
  | RequestDivisionsAction
  | RequestStoresAction
  | RequestLanesAction
  | RequestReportExportDataAction
  | ReceivedReportExportDataAction
  | RequestReportExportStatusListAction
  | ReceivedReportExportStatusListAction
  | RequestReportDownloadURL
  | ReceivedReportDownloadURL
  | ResetReportDownloadURL;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
const isResponseOk = (response: Response, dispatch: any): boolean => {
  if (response.ok) return true;
  if (response.status === 401) {
    dispatch({
      type: ReportsActionTypes.UNAUTHORIZED_REQUEST,
    });
  } else {
    dispatch({
      type: ReportsActionTypes.RECEIVED_HTTP_RESPONSE_ERROR_CODE,
      statusCode: response.status,
    });
  }
  return false;
};

export const actionCreators = {
  resetState: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
    const appState = getState();
    if (!appState) return;

    console.log(`Dispatching ${ReportsActionTypes.RESET_STATE}`);

    dispatch({
      type: ReportsActionTypes.RESET_STATE,
    });
  },

  resetFetchError:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ReportsActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR}`,
      );

      dispatch({
        type: ReportsActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR,
      });
    },

  fetchDivisions:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: ReportsActionTypes.REQUEST_DIVISIONS,
        refreshing: true,
      });

      const response = await fetch(
        `reports/getdivisions`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      dispatch({
        type: ReportsActionTypes.RECEIVED_DIVISIONS,
        divisions: result,
      });
    },

  fetchStores:
    (division: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: ReportsActionTypes.REQUEST_STORE_NUMBERS,
        refreshing: true,
      });

      const response = await fetch(
        `reports/GetSiteNumbers/${division}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      dispatch({
        type: ReportsActionTypes.RECEIVED_STORE_NUMBERS,
        stores: result,
      });
    },

  fetchLanesBySite:
    (siteNumber: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: ReportsActionTypes.REQUEST_LANE_NUMBERS,
        refreshing: true,
      });

      const response = await fetch(
        `reports/GetNodes/${siteNumber}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ReportNodeInfo[];

      dispatch({
        type: ReportsActionTypes.RECEIVED_LANE_NUMBERS,
        reportNodeInfo: result,
      });
    },

  fetchCategories:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `reports/getcategories`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ReportCategory[];

      dispatch({
        type: ReportsActionTypes.RECEIVED_REPORT_CATEGORIES,
        categories: result,
      });
    },

  fetchReportDataByNode:
    (reportQueryInfo: ReportQueryInfoByNode): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${ReportsActionTypes.REQUEST_REPORT_DATA}`);
      dispatch({
        type: ReportsActionTypes.REQUEST_REPORT_DATA,
      });

      let payload: ReportQueryInfoByNodePayload = {
        ReportID: reportQueryInfo.ReportID,
        SiteNumber: reportQueryInfo.SiteNumber,
        LaneNumbers: reportQueryInfo.LaneNumbers,
        StartDateTime: getDateWithoutTimezone(
          new Date(reportQueryInfo.StartDateTime),
        ),
        EndDateTime: getDateWithoutTimezone(
          new Date(reportQueryInfo.EndDateTime),
        ),
        Language: reportQueryInfo.Language,
      };

      const response = await fetch(
        `reports/getreportdatabynode`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          payload,
        ),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ReportQueryResult;

      console.log(`Dispatching ${ReportsActionTypes.RECEIVED_REPORT_DATA}`);
      dispatch({
        type: ReportsActionTypes.RECEIVED_REPORT_DATA,
        queryResult: result,
      });
    },

  fetchGridPageByNode:
    (reportQueryInfo: ReportQueryGridPageByNode): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${ReportsActionTypes.REQUEST_REPORT_DATA_PAGE}`);
      dispatch({
        type: ReportsActionTypes.REQUEST_REPORT_DATA_PAGE,
      });

      let payload: ReportQueryGridPageByNodePayload = {
        ReportID: reportQueryInfo.ReportID,
        SiteNumber: reportQueryInfo.SiteNumber,
        LaneNumbers: reportQueryInfo.LaneNumbers,
        StartDateTime: getDateWithoutTimezone(
          new Date(reportQueryInfo.StartDateTime),
        ),
        EndDateTime: getDateWithoutTimezone(
          new Date(reportQueryInfo.EndDateTime),
        ),
        Language: reportQueryInfo.Language,
        Skip: reportQueryInfo.Skip,
        Take: reportQueryInfo.Take,
        GridID: reportQueryInfo.GridID,
      };

      const response = await fetch(
        `reports/getreportpagedatabynode`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          payload,
        ),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ReportGridPageQueryResult;

      console.log(
        `Dispatching ${ReportsActionTypes.RECEIVED_REPORT_DATA_PAGE}`,
      );
      dispatch({
        type: ReportsActionTypes.RECEIVED_REPORT_DATA_PAGE,
        queryResult: result,
      });
    },

  fetchReportDataByStore:
    (reportQueryInfo: ReportQueryInfoByStore): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${ReportsActionTypes.REQUEST_REPORT_DATA}`);
      dispatch({
        type: ReportsActionTypes.REQUEST_REPORT_DATA,
      });

      let payload: ReportQueryInfoByStorePayload = {
        ReportID: reportQueryInfo.ReportID,
        SiteNumbers: reportQueryInfo.SiteNumbers,
        StartDateTime: getDateWithoutTimezone(
          new Date(reportQueryInfo.StartDateTime),
        ),
        EndDateTime: getDateWithoutTimezone(
          new Date(reportQueryInfo.EndDateTime),
        ),
        Language: reportQueryInfo.Language,
      };

      const response = await fetch(
        `reports/getreportdatabysite`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          payload,
        ),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ReportQueryResult;

      console.log(`Dispatching ${ReportsActionTypes.RECEIVED_REPORT_DATA}`);
      dispatch({
        type: ReportsActionTypes.RECEIVED_REPORT_DATA,
        queryResult: result,
      });
    },

  fetchGridPageByStore:
    (
      reportQueryInfo: ReportQueryGridPageByStore,
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${ReportsActionTypes.REQUEST_REPORT_DATA_PAGE}`);
      dispatch({
        type: ReportsActionTypes.REQUEST_REPORT_DATA_PAGE,
      });

      let payload: ReportQueryGridPageByStorePayload = {
        ReportID: reportQueryInfo.ReportID,
        SiteNumbers: reportQueryInfo.SiteNumbers,
        StartDateTime: getDateWithoutTimezone(
          new Date(reportQueryInfo.StartDateTime),
        ),
        EndDateTime: getDateWithoutTimezone(
          new Date(reportQueryInfo.EndDateTime),
        ),
        Language: reportQueryInfo.Language,
        Skip: reportQueryInfo.Skip,
        Take: reportQueryInfo.Take,
        GridID: reportQueryInfo.GridID,
      };

      const response = await fetch(
        `reports/getreportpagedatabysite`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          payload,
        ),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ReportGridPageQueryResult;

      console.log(
        `Dispatching ${ReportsActionTypes.RECEIVED_REPORT_DATA_PAGE}`,
      );
      dispatch({
        type: ReportsActionTypes.RECEIVED_REPORT_DATA_PAGE,
        queryResult: result,
      });
    },

  exportReportData:
    (requestBody: ReportExportRequestBody): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${ReportsActionTypes.REQUEST_REPORT_EXPORT_DATA}`,
      );
      dispatch({
        type: ReportsActionTypes.REQUEST_REPORT_EXPORT_DATA,
        isRequestingReportExportData: true,
      });

      let payload: ReportExportRequestBodyPayload = {
        ReportID: requestBody.ReportID,
        SiteNumbers: requestBody.SiteNumbers,
        LaneNumbers: requestBody.LaneNumbers,
        ReportStartDateTime: getDateWithoutTimezone(
          new Date(requestBody.ReportStartDateTime),
        ),
        ReportEndDateTime: getDateWithoutTimezone(
          new Date(requestBody.ReportEndDateTime),
        ),
        RequestedOn: getDateWithoutTimezone(new Date()),
        Language: requestBody.Language,
        ExportPeriodBreakdown: requestBody.ExportPeriodBreakdown,
        ReportExportSource: ReportExportSource.VisionIQ,
      };

      const response = await fetch(
        `reports/exportReportData`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          payload,
        ),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ReportExportResult;

      console.log(
        `Dispatching ${ReportsActionTypes.RECEIVED_REPORT_EXPORT_DATA_RESULT}`,
      );
      dispatch({
        type: ReportsActionTypes.RECEIVED_REPORT_EXPORT_DATA_RESULT,
        reportExportResult: result,
      });
    },

  fetchReportExportStatusList:
    (request: ReportExportListRequest): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: ReportsActionTypes.REQUEST_REPORT_EXPORT_STATUS_LIST,
        isRequesting: true,
      });

      const response = await fetch(
        `reports/getReportExportStatusList`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          request,
        ),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ReportExportStatusList;

      dispatch({
        type: ReportsActionTypes.RECEIVED_REPORT_EXPORT_STATUS_LIST,
        reportExportStatusList: result,
      });
    },

  setPanelBarVisible:
    (value: boolean): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      dispatch({
        type: ReportsActionTypes.SET_PANEL_BAR_VISIBLE,
        showPanelBar: value,
      });
    },

  fetchReportDownloadURL:
    (filePath: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: ReportsActionTypes.REQUEST_REPORT_DOWNLOAD_URL,
        isRequesting: true,
      });

      let payload: ReportDownloadURLRequestBody = {
        FilePath: filePath,
      };

      const response = await fetch(
        `reports/getReportDownloadURL`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          payload,
        ),
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as ReportDownloadURLResponseResult;

      dispatch({
        type: ReportsActionTypes.RECEIVED_REPORT_DOWNLOAD_URL,
        responseResult: result,
      });
    },

  resetReportDownloadURL:
    (): AppThunkAction<KnownAction> => (dispatch, getState) => {
      dispatch({
        type: ReportsActionTypes.RESET_REPORT_DOWNLOAD_URL,
      });
    },
};

const initialReportExportStatusList: ReportExportStatus[] = [];
// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const unloadedState: ReportsState = {
  unauthorizedRequest: false,
  fetchError: { StatusCode: 0, Message: '' },
  isFetchingReportData: false,
  divisions: [],
  stores: [],
  reportNodeInfo: [],
  categories: [],
  reportData: {} as ReportQueryResult,
  panelBarVisible: false,
  isFetchingDivisions: false,
  isFetchingStores: false,
  isFetchingReportNodeInfo: false,
  isRequestingReportExportData: false,
  reportExportResult: {} as ReportExportResult,
  isFetchingReportExportStatusList: false,
  reportExportStatusList: initialReportExportStatusList,
  isExportResultReceived: false,
  isFetchingReportDownloadURL: false,
  reportDownloadURL: {} as ReportDownloadURLResponseResult,
};

export const reducer: Reducer<ReportsState> = (
  state: ReportsState | undefined,
  incomingAction: Action,
): ReportsState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case ReportsActionTypes.RESET_STATE:
      return unloadedState;

    case ReportsActionTypes.UNAUTHORIZED_REQUEST:
      return {
        ...state,
        unauthorizedRequest: true,
      };
    case ReportsActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR:
      let resetData = { StatusCode: 0, Message: '' };
      return {
        ...state,
        isFetchingReportData: false,
        fetchError: resetData,
        isFetchingDivisions: false,
        isFetchingReportNodeInfo: false,
        isFetchingStores: false,
      };
    case ReportsActionTypes.RECEIVED_HTTP_RESPONSE_ERROR_CODE:
      let fetchErrorData = {
        StatusCode: action.statusCode,
        Message: 'FETCH_ERROR_MESSAGE_REPORTS',
      };
      return {
        ...state,
        isFetchingReportData: false,
        fetchError: fetchErrorData,
        isFetchingDivisions: false,
        isFetchingReportNodeInfo: false,
        isFetchingStores: false,
      };
    case ReportsActionTypes.RECEIVED_DIVISIONS:
      return {
        ...state,
        divisions: action.divisions,
        isFetchingDivisions: false,
      };
    case ReportsActionTypes.RECEIVED_STORE_NUMBERS:
      return {
        ...state,
        stores: action.stores,
        isFetchingStores: false,
      };
    case ReportsActionTypes.RECEIVED_LANE_NUMBERS:
      return {
        ...state,
        reportNodeInfo: action.reportNodeInfo,
        isFetchingReportNodeInfo: false,
      };
    case ReportsActionTypes.RECEIVED_REPORT_CATEGORIES:
      return {
        ...state,
        categories: action.categories,
      };
    case ReportsActionTypes.REQUEST_REPORT_DATA:
      return {
        ...state,
        isFetchingReportData: true,
      };
    case ReportsActionTypes.RECEIVED_REPORT_DATA:
      return {
        ...state,
        isFetchingReportData: false,
        reportData: action.queryResult,
      };
    case ReportsActionTypes.RECEIVED_REPORT_DATA_PAGE:
      return {
        ...state,
        reportData: {
          ...state.reportData,
          Sections: state.reportData.Sections.map(section => ({
            ...section,
            Grids: section.Grids.map(grid =>
              grid.Id === action.queryResult.GridID
                ? {
                    ...grid,
                    Rows: action.queryResult.Rows,
                    Skip: action.queryResult.Skip,
                    Take: action.queryResult.Take,
                  }
                : grid,
            ),
          })),
          GeneratedDateTime: action.queryResult.GeneratedDateTime,
        },
      };
    case ReportsActionTypes.SET_PANEL_BAR_VISIBLE:
      return {
        ...state,
        panelBarVisible: action.showPanelBar,
      };
    case ReportsActionTypes.REQUEST_DIVISIONS:
      return {
        ...state,
        isFetchingDivisions: action.refreshing,
      };
    case ReportsActionTypes.REQUEST_STORE_NUMBERS:
      return {
        ...state,
        isFetchingStores: action.refreshing,
      };
    case ReportsActionTypes.REQUEST_LANE_NUMBERS:
      return {
        ...state,
        isFetchingReportNodeInfo: action.refreshing,
      };
    case ReportsActionTypes.REQUEST_REPORT_EXPORT_DATA:
      return {
        ...state,
        isExportResultReceived: false,
        isRequestingReportExportData: action.isRequestingReportExportData,
      };
    case ReportsActionTypes.RECEIVED_REPORT_EXPORT_DATA_RESULT:
      return {
        ...state,
        isExportResultReceived: true,
        isRequestingReportExportData: false,
        reportExportResult: action.reportExportResult,
      };
    case ReportsActionTypes.REQUEST_REPORT_EXPORT_STATUS_LIST:
      return {
        ...state,
        isFetchingReportExportStatusList: action.isRequesting,
      };
    case ReportsActionTypes.RECEIVED_REPORT_EXPORT_STATUS_LIST:
      return {
        ...state,
        isFetchingReportExportStatusList: false,
        reportExportStatusList: action.reportExportStatusList.Data,
      };
    case ReportsActionTypes.REQUEST_REPORT_DOWNLOAD_URL:
      return {
        ...state,
        isFetchingReportDownloadURL: action.isRequesting,
      };
    case ReportsActionTypes.RECEIVED_REPORT_DOWNLOAD_URL:
      return {
        ...state,
        isFetchingReportDownloadURL: false,
        reportDownloadURL: action.responseResult,
      };
    case ReportsActionTypes.RESET_REPORT_DOWNLOAD_URL:
      return {
        ...state,
        isFetchingReportDownloadURL: false,
        reportDownloadURL: {} as ReportDownloadURLResponseResult,
      };
    default:
      break;
  }
  return state;
};
